import { Children, useState } from 'react';
import PropTypes from 'prop-types';

import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';

import isEmpty from 'lodash/fp/isEmpty';
import noop from 'lodash/fp/noop';

import { Box, Text, theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import { ReactComponent as ArrowLeftIcon } from 'assets/images/icon_arrow_left_no_tail.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/images/icon_arrow_right_no_tail.svg';
import ArrowLeftIconB from 'assets/images/left-arrow.svg';
import ArrowRightIconB from 'assets/images/right-arrow.svg';

import SliderControls from './SliderControls';

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const Root = styled.div`
  position: relative;
  flex-grow: 1;

  width: var(--root-width);
  height: var(--root-height);

  background: transparent;
  filter: var(--root-bright);
  padding: var(--root-padding);
`;

const ArrowButton = styled.button`
  height: ${legacyTheme.spacing.s16};

  background-color: transparent;
  opacity: 0.5;
  border: none;
  appearance: none;

  &:first-of-type {
    margin: 0 auto 0 0;
  }
  &:last-of-type {
    margin: 0 0 0 auto;
  }
  &:enabled {
    cursor: pointer;
    opacity: 1;
  }
`;

const SectionTitleControl = styled.button`
  all: unset;
  display: flex;
  border-bottom: ${({ isSelected }) => isSelected && `3px solid ${theme.colors.accent[300]}`};
  cursor: pointer;
`;

const NextButton = styled.button`
  position: absolute;
  top: ${({ hasSectionsTitles }) => (hasSectionsTitles ? '50%' : '30%')};
  right: -25%;

  background: none;
  border: none;
  cursor: pointer;
`;

const PrevButton = styled.button`
  position: absolute;
  top: ${({ hasSectionsTitles }) => (hasSectionsTitles ? '50%' : '30%')};
  left: -25%;

  background: none;
  border: none;
  appearance: none;
  cursor: pointer;
`;

// XXX: requires to refactor SliderControls in order to get rid of this
const useStyles = makeStyles({
  controls: {
    width: '100%',
  },
  controlsWithPadding: {
    width: '100%',
    padding: `${legacyTheme.spacing.s16} 0`,
  },
  floatingControls: {
    width: '100%',
    position: 'absolute',
    bottom: 20,
    zIndex: 2,
  },
  whiteControls: {
    position: 'absolute',
    bottom: 0,
    margin: 'auto',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    '& button': {
      opacity: 0.5,
      background: 'white',
      '& svg': {
        fill: 'white',
        '& path': {
          stroke: 'white',
        },
      },
    },
    '& button.arrowButton': {
      background: 'transparent',
    },
  },
});

const getCurrentIndex = (currentIndex, maxIndex) =>
  ((currentIndex % maxIndex) + maxIndex) % maxIndex;

const slideRendererHOC = (childrenArray, childrenCount) => {
  const SlideRenderer = ({ index, key }) => (
    <div key={key}>{childrenArray[getCurrentIndex(index, childrenCount)]}</div>
  );
  SlideRenderer.propTypes = {
    index: PropTypes.number.isRequired,
    key: PropTypes.number.isRequired,
  };
  return SlideRenderer;
};

const Carousel = ({
  arrows,
  bigArrows,
  children,
  controlsPosition,
  controlsTheme,
  dataFrom,
  floatingControls,
  sectionsTitlesControl,
  isInfinite,
  isLooping,
  step,
  whiteControls,
  nextHighlight,
  isBright,
  width,
  height,
  hasControlsPadding,
  className,
  onAfterChange,
}) => {
  const { classes } = useStyles();
  const [activeStep, setActiveStep] = useState(step);

  const maxSteps = Children.count(children);
  const childrenArray = Children.toArray(children);
  const getClassForControlsFlag = () => {
    if (whiteControls) {
      return classes.whiteControls;
    }
    if (floatingControls) {
      return classes.floatingControls;
    }

    if (hasControlsPadding) {
      return classes.controlsWithPadding;
    }
    return classes.controls;
  };

  const updateStep = index => {
    setActiveStep(index);
    onAfterChange?.();
  };

  return (
    <Root
      className={className}
      style={{
        '--root-width': nextHighlight > 0 ? `calc(${width} + ${nextHighlight}px * 4}` : `${width}`,
        '--root-height': height ? `${height}px` : 'initial',
        '--root-padding': nextHighlight > 0 ? `0 ${nextHighlight}px` : 'initial',
        '--root-bright': isBright ? 'invert(1)' : 'initial',
      }}
    >
      {sectionsTitlesControl && (
        <>
          <Box
            borderBottom="1px"
            borderColor="neutral.400"
            display="flex"
            gap={{ desktop: '15x', mobile: '10x' }}
            marginLeft={{ desktop: 'none', mobile: '6x' }}
          >
            {sectionsTitlesControl.map((header, index) => (
              <SectionTitleControl
                key={header.title}
                data-click={header.dataClick}
                data-from={header.dataFrom}
                isSelected={index === activeStep}
                onClick={() => updateStep(index)}
              >
                <Text as="h3" color="primary.300" paddingBottom="3x" variant="label1medium">
                  {header.title}
                </Text>
              </SectionTitleControl>
            ))}
          </Box>
        </>
      )}
      {isInfinite ? (
        <VirtualizeSwipeableViews
          enableMouseEvents
          index={activeStep}
          onChangeIndex={updateStep}
          slideRenderer={slideRendererHOC(childrenArray, maxSteps)}
          slideStyle={{ width: '100%' }}
          style={nextHighlight > 0 ? { padding: `0 ${nextHighlight}px` } : null}
        />
      ) : (
        <SwipeableViews
          enableMouseEvents
          index={activeStep}
          onChangeIndex={updateStep}
          style={nextHighlight > 0 ? { padding: `0 ${nextHighlight}px` } : null}
        >
          {Children.map(children, (child, index) => (
            <div>{Math.abs(activeStep - index) <= 3 && child}</div>
          ))}
        </SwipeableViews>
      )}
      {maxSteps > 1 && (
        <SliderControls
          arrowLeft={
            arrows && (
              <ArrowButton
                className="arrowButton"
                data-click="change-slide-arrow"
                data-from={dataFrom}
                disabled={activeStep <= 0 && !isLooping && !isInfinite}
                onClick={() =>
                  isInfinite ? updateStep(activeStep - 1) : updateStep((activeStep || maxSteps) - 1)
                }
                type="button"
              >
                <ArrowLeftIcon title="Left Arrow" />
              </ArrowButton>
            )
          }
          arrowRight={
            arrows && (
              <ArrowButton
                className="arrowButton"
                data-click="change-slide-arrow"
                data-from={dataFrom}
                disabled={activeStep >= maxSteps - 1 && !isLooping && !isInfinite}
                onClick={() =>
                  isInfinite ? updateStep(activeStep + 1) : updateStep((activeStep + 1) % maxSteps)
                }
                type="button"
              >
                <ArrowRightIcon title="Right Arrow" />
              </ArrowButton>
            )
          }
          classes={{
            root: `${getClassForControlsFlag()}`,
          }}
          currentIndex={getCurrentIndex(activeStep, maxSteps)}
          dataFrom={dataFrom}
          noFixedHeight={!hasControlsPadding}
          onClick={isInfinite ? noop : updateStep}
          position={controlsPosition}
          size={maxSteps}
          theme={controlsTheme}
        />
      )}
      {bigArrows && (
        <>
          <PrevButton
            data-click="change-slide-arrow"
            data-from={dataFrom}
            hasSectionsTitles={!isEmpty(sectionsTitlesControl)}
            onClick={() => updateStep((activeStep || maxSteps) - 1)}
            type="button"
          >
            <img
              alt="Carousel previous slide - vector"
              data-click="change-slide-arrow"
              data-from="hero"
              src={ArrowLeftIconB}
              title="Left Arrow"
            />
          </PrevButton>
          <NextButton
            data-click="change-slide-arrow"
            data-from={dataFrom}
            hasSectionsTitles={!isEmpty(sectionsTitlesControl)}
            onClick={() => updateStep((activeStep + 1) % maxSteps)}
            type="button"
          >
            <img
              alt="Carousel next slide - vector"
              data-click="change-slide-arrow"
              data-from="hero"
              src={ArrowRightIconB}
              title="Right Arrow"
            />
          </NextButton>
        </>
      )}
    </Root>
  );
};

Carousel.propTypes = {
  arrows: PropTypes.bool,
  bigArrows: PropTypes.bool,
  children: PropTypes.node.isRequired,
  dataFrom: PropTypes.string,
  width: PropTypes.string,
  nextHighlight: PropTypes.number,
  isInfinite: PropTypes.bool,
  controlsPosition: PropTypes.string,
  controlsTheme: PropTypes.string,
  whiteControls: PropTypes.bool,
  floatingControls: PropTypes.bool,
  isLooping: PropTypes.bool,
  step: PropTypes.number,
  isBright: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  className: PropTypes.string,
  sectionsTitlesControl: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      dataClick: PropTypes.string,
      dataFrom: PropTypes.string,
    })
  ),
  hasControlsPadding: PropTypes.boolean,
  onAfterChange: PropTypes.func,
};

Carousel.defaultProps = {
  arrows: true,
  bigArrows: false,
  controlsPosition: null,
  controlsTheme: 'noir',
  dataFrom: 'Carousel',
  floatingControls: false,
  isInfinite: false,
  isLooping: false,
  step: 0,
  nextHighlight: 0,
  width: 'auto',
  whiteControls: false,
  isBright: false,
  height: null,
  className: null,
  sectionsTitlesControl: null,
  hasControlsPadding: true,
  onAfterChange: () => {},
};

export default Carousel;
